import React from 'react'
import { useParams } from 'react-router-dom'

import { useClassroomEnv } from '@components/ClassroomEnv'

import { ClassroomManager, fscreen } from '../../../helpers'
import { useClassroomDispatch } from './index'
import { useAuth } from '@hooks'

export const useClassroomManager = () => {
  const classroomDispatch = useClassroomDispatch()
  const params = useParams()
  const { profile, token } = useAuth()
  const { env } = useClassroomEnv()

  const handelFullscreenChange = React.useCallback(() => {
    if (fscreen.fullscreenElement === null && ClassroomManager.getFullscreen())
      ClassroomManager.minimize(ClassroomManager.getFullscreen())
  }, [])

  const maximize = React.useCallback(async () => {
    const elem = document.querySelector(`.classroom-components`)

    try {
      await fscreen.requestFullscreen(elem)
      fscreen.addEventListener('fullscreenchange', handelFullscreenChange)
    } catch (err) {
      if (err.message === 'fullscreen error')
        classroomDispatch({ type: 'open_fullscreen_modal' })
    }
  }, [classroomDispatch, handelFullscreenChange])

  const onFullscreenChange = React.useCallback(() => {
    const panel = ClassroomManager.getFullscreen()
    if (panel && fscreen.fullscreenElement === null) {
      maximize()
    } else if (!panel && fscreen.fullscreenElement !== null) {
      fscreen.exitFullscreen()
      fscreen.removeEventListener('fullscreenchange', handelFullscreenChange)
    }
  }, [maximize, handelFullscreenChange])

  const onPanelsChange = React.useCallback(() => {
    classroomDispatch({
      type: 'set_panels',
      payload: ClassroomManager.getPanels()
    })
    ClassroomManager.updatePanels()
  }, [classroomDispatch])

  const initialize = () => {
    ClassroomManager.init({
      id: params.id || profile.id,
      user: profile,
      env,
      token
    })
    classroomDispatch({
      type: 'set_panels',
      payload: ClassroomManager.getPanels()
    })
    ClassroomManager.observe('panels', onPanelsChange)
    ClassroomManager.observe('fullscreen', onFullscreenChange)
  }

  const destroy = React.useCallback(() => {
    // This method may be called if classroom is not initialized.
    // So we need to be sure if instance exists before destroying.
    if (!ClassroomManager.classroomId) return

    ClassroomManager.unobserve('panels', onPanelsChange)
    ClassroomManager.unobserve('fullscreen', onFullscreenChange)
    ClassroomManager.destroy()
  }, [onFullscreenChange, onPanelsChange])

  return { initialize, destroy, maximize }
}
